import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Chip, Avatar } from '@mui/material';
import {
  MicOff as MicOffIcon,
  ScreenShare as ScreenShareIcon,
  PanTool as HandIcon
} from '@mui/icons-material';

/**
 * Video tile component for displaying participant video
 */
function VideoTile({ 
  participant, 
  stream, 
  isLocal = false, 
  isActiveSpeaker = false,
  audioLevel = 0
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const showVideo = stream && !participant.isCameraOff;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        bgcolor: '#1a1a1a',
        borderRadius: 1,
        overflow: 'hidden',
        border: isActiveSpeaker ? '3px solid #1976d2' : '1px solid #444',
        transition: 'border-color 0.3s ease',
        '&::after': audioLevel > 20 ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: '3px solid rgba(25, 118, 210, 0.5)',
          borderRadius: 1,
          pointerEvents: 'none',
          animation: 'pulse 0.5s ease-in-out'
        } : {}
      }}
    >
      {/* Video element */}
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: isLocal ? 'scaleX(-1)' : 'none' // Mirror local video
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: 32,
              bgcolor: '#1976d2'
            }}
          >
            {participant.displayName ? participant.displayName.charAt(0).toUpperCase() : '?'}
          </Avatar>
        </Box>
      )}

      {/* Participant name */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          borderRadius: 1,
          px: 1,
          py: 0.5
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}
        >
          {participant.displayName} {isLocal && '(You)'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          {participant.isMuted && (
            <MicOffIcon sx={{ fontSize: 16, color: 'error.main' }} />
          )}
          {participant.isScreenSharing && (
            <ScreenShareIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          )}
          {participant.isHandRaised && (
            <HandIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          )}
        </Box>
      </Box>

      {/* Role badge */}
      {participant.role === 'host' && (
        <Chip
          label="Host"
          size="small"
          color="primary"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: 10
          }}
        />
      )}
    </Box>
  );
}

/**
 * Responsive grid layout for video tiles
 * Automatically adjusts based on number of participants
 */
function VideoGrid({ 
  localParticipant, 
  localStream,
  remoteParticipants, 
  remoteStreams,
  activeSpeakerId,
  audioLevels 
}) {
  const [gridLayout, setGridLayout] = useState({ rows: 1, cols: 1 });

  const totalParticipants = 1 + remoteParticipants.length; // local + remote

  useEffect(() => {
    // Calculate optimal grid layout
    if (totalParticipants === 1) {
      setGridLayout({ rows: 1, cols: 1 });
    } else if (totalParticipants === 2) {
      setGridLayout({ rows: 1, cols: 2 });
    } else if (totalParticipants <= 4) {
      setGridLayout({ rows: 2, cols: 2 });
    } else if (totalParticipants <= 6) {
      setGridLayout({ rows: 2, cols: 3 });
    } else if (totalParticipants <= 9) {
      setGridLayout({ rows: 3, cols: 3 });
    } else if (totalParticipants <= 12) {
      setGridLayout({ rows: 3, cols: 4 });
    } else {
      setGridLayout({ rows: 4, cols: 4 });
    }
  }, [totalParticipants]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'grid',
        gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
        gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
        gap: 1,
        p: 2,
        overflow: 'auto'
      }}
    >
      {/* Local participant tile */}
      <VideoTile
        participant={localParticipant}
        stream={localStream}
        isLocal={true}
        isActiveSpeaker={false}
        audioLevel={0}
      />

      {/* Remote participant tiles */}
      {remoteParticipants.map((participant) => (
        <VideoTile
          key={participant.participantId}
          participant={participant}
          stream={remoteStreams.get(participant.peerId)}
          isLocal={false}
          isActiveSpeaker={participant.peerId === activeSpeakerId}
          audioLevel={audioLevels.get(participant.peerId) || 0}
        />
      ))}
    </Box>
  );
}

export { VideoTile, VideoGrid };
export default VideoGrid;
